import { useEffect, useMemo, useState } from "react";
import type { Settings } from "@vereinfach/types";
import { ApiError, errorMessage, mutate } from "../../lib/api";
import { useSettings as useSettingsContext } from "../../lib/settings";
import { useCurrentTenant } from "../../lib/currentTenant";
import { Field, FieldGroup, SaveBar, Section, useSavedFlash } from "./common";

type IbanKind = "qr" | "scor" | "none";

/** Local classification only — the server validates authoritatively on save. */
function classifyIban(raw: string): IbanKind {
  const iban = raw.replace(/\s/g, "").toUpperCase();
  if (!/^(CH|LI)\d{19}$/.test(iban)) return "none";
  if (mod97(toNumericString(iban.slice(4) + iban.slice(0, 4))) !== 1) return "none";
  const iid = Number(iban.slice(4, 9));
  return iid >= 30000 && iid <= 31999 ? "qr" : "scor";
}

function toNumericString(s: string): string {
  return s.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
}

function mod97(s: string): number {
  let r = 0;
  for (let i = 0; i < s.length; i++) r = (r * 10 + (s.charCodeAt(i) - 48)) % 97;
  return r;
}

const VAT_PRESETS: { bp: number; label: string }[] = [
  { bp: 810, label: "8.1 % (Normalsatz)" },
  { bp: 770, label: "7.7 % (alt)" },
  { bp: 260, label: "2.6 % (reduziert)" },
];

/** basis points -> "8.1" */
function bpToPercent(bp: number): string {
  return (bp / 100).toString();
}

const SETTINGS_ERRORS: Record<string, string> = {
  vat_uid_required: "Für die MwSt wird eine gültige UID (CHE-…) benötigt.",
  vat_rate_required: "Für die MwSt wird ein Steuersatz grösser als 0 benötigt.",
  qr_iban_missing: "Für QR-Rechnung fehlt die IBAN.",
  qr_iban_invalid: "Die IBAN ist ungültig oder keine CH/LI-IBAN.",
  qr_creditor_name_missing: "Für QR-Rechnung fehlt der Vereinsname (Einstellungen → Verein).",
  qr_creditor_address_missing:
    "Für QR-Rechnung fehlt die Strasse des Vereins in Einstellungen → Verein.",
  qr_creditor_zip_missing: "Für QR-Rechnung fehlt die PLZ des Vereins in Einstellungen → Verein.",
  qr_creditor_city_missing: "Für QR-Rechnung fehlt der Ort des Vereins in Einstellungen → Verein.",
};

function friendlyError(e: unknown): string {
  if (e instanceof ApiError && typeof e.data === "object" && e.data) {
    const code = e.data.error as string;
    // VAT validation arrives as invalid_input + a detail discriminator.
    const detail = e.data.detail as string | undefined;
    if (code === "invalid_input" && detail && SETTINGS_ERRORS[detail])
      return SETTINGS_ERRORS[detail];
    if (code && SETTINGS_ERRORS[code]) return SETTINGS_ERRORS[code];
  }
  return errorMessage(e);
}

export function SettingsRechnungen() {
  const { settings, refetch: refetchSettings } = useSettingsContext();
  const tenant = useCurrentTenant();

  const [local, setLocal] = useState<Settings | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Nummernkreis (separate, rarely used action)
  const [seqOpen, setSeqOpen] = useState(false);
  const [seqInput, setSeqInput] = useState("");
  const [seqSaving, setSeqSaving] = useState(false);
  const [seqSavedAt, setSeqSavedAt] = useState<number | null>(null);
  const [seqError, setSeqError] = useState<string | null>(null);
  const seqFlash = useSavedFlash(seqSavedAt);

  useEffect(() => {
    if (settings) setLocal(settings);
  }, [settings]);

  function set<K extends keyof Settings>(k: K, v: Settings[K]) {
    setLocal((s) => (s ? { ...s, [k]: v } : s));
  }

  const ibanKind = useMemo(() => classifyIban(local?.iban ?? ""), [local?.iban]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!local) return;
    setSaving(true);
    setError(null);
    try {
      await mutate<Settings>(
        `/tenants/${tenant.id}/settings`,
        {
          invoice_payment_mode: local.invoice_payment_mode,
          iban: local.iban,
          invoice_lead_days: Number.isFinite(local.invoice_lead_days)
            ? local.invoice_lead_days
            : 30,
          payment_terms_days: Number.isFinite(local.payment_terms_days)
            ? local.payment_terms_days
            : 30,
          vat_enabled: local.vat_enabled,
          vat_rate_bp: Number.isFinite(local.vat_rate_bp) ? local.vat_rate_bp : 0,
          vat_mode: local.vat_mode,
          vat_number: local.vat_number,
        },
        "PATCH",
      );
      await refetchSettings();
      setSavedAt(Date.now());
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  async function onSeqSubmit() {
    if (!local) return;
    const next = parseInt(seqInput, 10);
    if (!Number.isFinite(next) || next <= local.next_invoice_seq) {
      setSeqError("Nur erhöhen möglich.");
      return;
    }
    setSeqSaving(true);
    setSeqError(null);
    try {
      await mutate<Settings>(`/tenants/${tenant.id}/settings`, { next_invoice_seq: next }, "PATCH");
      await refetchSettings();
      setSeqInput("");
      setSeqOpen(false);
      setSeqSavedAt(Date.now());
    } catch (err) {
      setSeqError(friendlyError(err));
    } finally {
      setSeqSaving(false);
    }
  }

  if (!local) return <p className="text-slate-400">Lädt…</p>;

  const year = new Date().getFullYear();
  const nextNumber = `${year}-${String(local.next_invoice_seq).padStart(4, "0")}`;

  return (
    <div className="space-y-8">
      <form onSubmit={onSubmit} className="space-y-8">
        <Section title="Zahlungsart">
          <FieldGroup label="Zahlungsart">
            <div className="mt-1 flex flex-col gap-2 text-sm">
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="invoice_payment_mode"
                  checked={local.invoice_payment_mode === "qr"}
                  onChange={() => set("invoice_payment_mode", "qr")}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium">QR-Rechnung (empfohlen)</span>
                  <span className="block text-xs text-slate-500">
                    Mit Schweizer QR-Einzahlungsschein. Benötigt eine gültige CH/LI-IBAN und die
                    vollständige Vereinsadresse (Einstellungen → Verein).
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="invoice_payment_mode"
                  checked={local.invoice_payment_mode === "standard"}
                  onChange={() => set("invoice_payment_mode", "standard")}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium">Nur Zahlungsinformationen (Text)</span>
                  <span className="block text-xs text-slate-500">
                    Ohne QR-Einzahlungsschein. Die Rechnung enthält die Zahlungsangaben als Text.
                  </span>
                </span>
              </label>
            </div>
          </FieldGroup>

          <Field label="IBAN">
            <input
              value={local.iban ?? ""}
              onChange={(e) => set("iban", e.target.value || null)}
              placeholder="CH00 0000 0000 0000 0000 0"
              className="input font-mono text-sm"
            />
            {ibanKind === "qr" && (
              <p className="mt-1 text-xs text-emerald-600">
                ✓ QR-IBAN erkannt — Rechnungen erhalten eine QR-Referenz
              </p>
            )}
            {ibanKind === "scor" && (
              <p className="mt-1 text-xs text-emerald-600">
                ✓ Normale IBAN — Rechnungen erhalten eine Creditor Reference (RF…)
              </p>
            )}
            {ibanKind === "none" && (
              <p className="mt-1 text-xs text-slate-500">
                CH- oder LI-IBAN deines Vereinskontos. Eine QR-IBAN erhältst du von deiner Bank.
              </p>
            )}
          </Field>
        </Section>

        <Section title="Rechnungsstellung">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Rechnungen erstellen … Tage vor Verlängerung">
              <input
                type="number"
                min={0}
                max={120}
                value={Number.isFinite(local.invoice_lead_days) ? local.invoice_lead_days : ""}
                onChange={(e) => set("invoice_lead_days", parseInt(e.target.value, 10))}
                placeholder="30"
                className="input"
              />
            </Field>
            <Field label="Zahlbar innert … Tagen">
              <input
                type="number"
                min={0}
                max={365}
                value={Number.isFinite(local.payment_terms_days) ? local.payment_terms_days : ""}
                onChange={(e) => set("payment_terms_days", parseInt(e.target.value, 10))}
                placeholder="30"
                className="input"
              />
            </Field>
          </div>
        </Section>

        <Section title="MwSt">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={local.vat_enabled}
              onChange={(e) => set("vat_enabled", e.target.checked)}
            />
            <span>
              <span className="font-medium">Mehrwertsteuer ausweisen</span>
              <span className="block text-xs text-slate-500">
                Mitgliederbeiträge sind in der Regel mehrwertsteuerbefreit. Aktiviere die MwSt nur,
                wenn dein Verein steuerpflichtig ist.
              </span>
            </span>
          </label>

          {local.vat_enabled && (
            <>
              <FieldGroup label="Steuersatz">
                <div className="flex flex-wrap items-center gap-2">
                  {VAT_PRESETS.map((p) => (
                    <button
                      key={p.bp}
                      type="button"
                      onClick={() => set("vat_rate_bp", p.bp)}
                      className={
                        "rounded-lg border px-3 py-1.5 text-sm " +
                        (local.vat_rate_bp === p.bp
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 hover:bg-slate-50")
                      }
                    >
                      {p.label}
                    </button>
                  ))}
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step="0.1"
                      value={
                        Number.isFinite(local.vat_rate_bp) ? bpToPercent(local.vat_rate_bp) : ""
                      }
                      onChange={(e) => {
                        const pct = parseFloat(e.target.value);
                        set("vat_rate_bp", Number.isFinite(pct) ? Math.round(pct * 100) : 0);
                      }}
                      placeholder="z. B. 8.1"
                      className="input w-24"
                    />
                    <span className="text-sm text-slate-500">%</span>
                  </div>
                </div>
              </FieldGroup>

              <FieldGroup label="Modus">
                <div className="mt-1 flex flex-col gap-2 text-sm">
                  <label className="flex items-start gap-2">
                    <input
                      type="radio"
                      name="vat_mode"
                      checked={local.vat_mode === "incl"}
                      onChange={() => set("vat_mode", "incl")}
                      className="mt-1"
                    />
                    <span>
                      <span className="font-medium">Inklusive</span>
                      <span className="block text-xs text-slate-500">
                        Die MwSt ist im Beitragsbetrag enthalten und wird auf der Rechnung
                        ausgewiesen.
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2">
                    <input
                      type="radio"
                      name="vat_mode"
                      checked={local.vat_mode === "excl"}
                      onChange={() => set("vat_mode", "excl")}
                      className="mt-1"
                    />
                    <span>
                      <span className="font-medium">Exklusive</span>
                      <span className="block text-xs text-slate-500">
                        Die MwSt wird zum Beitragsbetrag hinzugerechnet.
                      </span>
                    </span>
                  </label>
                </div>
              </FieldGroup>

              <Field
                label="UID (Pflicht)"
                help="Schweizer Unternehmens-Identifikationsnummer im Format CHE-123.456.789."
              >
                <input
                  value={local.vat_number ?? ""}
                  onChange={(e) => set("vat_number", e.target.value || null)}
                  placeholder="CHE-123.456.789"
                  className="input font-mono text-sm"
                />
              </Field>
            </>
          )}
        </Section>

        <SaveBar saving={saving} savedAt={savedAt} error={error} />
      </form>

      <Section title="Nummernkreis">
        <p className="text-sm">
          Nächste Rechnungsnummer: <span className="font-mono font-medium">{nextNumber}</span>
          {seqFlash && <span className="ml-2 text-xs text-emerald-600">Gespeichert.</span>}
        </p>
        {!seqOpen ? (
          <button
            type="button"
            onClick={() => setSeqOpen(true)}
            className="text-sm text-slate-500 underline underline-offset-2 hover:text-slate-900"
          >
            Nummernkreis fortsetzen
          </button>
        ) : (
          <FieldGroup
            label="Nummernkreis fortsetzen"
            help="Nur erhöhen möglich — für Vereine, die eine bestehende Serie weiterführen."
          >
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={local.next_invoice_seq + 1}
                value={seqInput}
                onChange={(e) => setSeqInput(e.target.value)}
                placeholder={String(local.next_invoice_seq)}
                className="input w-32"
              />
              <button
                type="button"
                onClick={onSeqSubmit}
                disabled={seqSaving}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
              >
                {seqSaving ? "Speichern…" : "Übernehmen"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSeqOpen(false);
                  setSeqInput("");
                  setSeqError(null);
                }}
                className="text-sm text-slate-500 hover:text-slate-900"
              >
                Abbrechen
              </button>
            </div>
            {seqError && <p className="mt-1 text-xs text-red-600">{seqError}</p>}
          </FieldGroup>
        )}
      </Section>
    </div>
  );
}
